import { useCallback, useContext, useEffect, useState } from 'react';
import { whatsappAccountService, WhatsappAccount } from '@/services/whatsapp-account.service';
import { AuthContext } from '@/contexts/AuthContext';
import { tokenStorage } from '@/utils/jwt';

export interface ActiveAccountState {
  loading: boolean;
  accounts: WhatsappAccount[];
  active: WhatsappAccount | null;
  refresh: () => Promise<void>;
  switchTo: (accountUid: string) => Promise<void>;
}

let cachedAccounts: WhatsappAccount[] | null = null;
let cachedSession: string | null = null;
const listeners = new Set<(accounts: WhatsappAccount[]) => void>();

function broadcast(accounts: WhatsappAccount[], session: string | null) {
  if (session !== tokenStorage.get()) return;
  cachedSession = session;
  cachedAccounts = accounts;
  listeners.forEach((l) => l(accounts));
}

async function fetchAccounts() {
  const session = tokenStorage.get();
  if (!session) { broadcast([], null); return []; }
  try {
    const list = await whatsappAccountService.list();
    broadcast(list, session);
    return list;
  } catch {
    // Keep a valid tenant cache during a temporary network outage.
    return cachedSession === session ? cachedAccounts ?? [] : [];
  }
}

export function useActiveAccount(): ActiveAccountState {
  const auth = useContext(AuthContext);
  const companyUid = auth?.user?.companyUid;
  if (cachedSession !== tokenStorage.get()) { cachedAccounts = null; cachedSession = tokenStorage.get(); }
  const [accounts, setAccounts] = useState<WhatsappAccount[]>(cachedAccounts ?? []);
  const [loading, setLoading] = useState<boolean>(cachedAccounts === null);

  useEffect(() => {
    const onChange = (list: WhatsappAccount[]) => setAccounts(list.filter(a => a.companyUid === companyUid));
    listeners.add(onChange);
    if (cachedAccounts !== null) onChange(cachedAccounts);
    if (cachedAccounts === null) {
      setLoading(true);
      fetchAccounts().finally(() => setLoading(false));
    }
    return () => { listeners.delete(onChange); };
  }, [companyUid]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await fetchAccounts();
    setLoading(false);
  }, []);

  const switchTo = useCallback(async (accountUid: string) => {
    await whatsappAccountService.setDefault(accountUid);
    await fetchAccounts();
  }, []);

  const scopedAccounts = accounts.filter(a => a.companyUid === companyUid);
  const active = scopedAccounts.find((a) => a.isDefault) ?? null;
  return { loading, accounts: scopedAccounts, active, refresh, switchTo };
}
